import Big from 'big.js';
import * as bootstrap from 'bootstrap';
import Konva from 'konva';
import { GetOptionPrice, GetPrice, GetSizes } from 'smg/customer-api/lineitems';
import * as models from 'smg/customer-api/models';
import { CustomizerEngine, DesignState, ImageLayer, QRModal } from 'smg/customizer';
import { SetupBuilderKeyboardShortcuts } from 'smg/customizer/keyboard-shortcuts';
import { BuilderShareDesign } from 'smg/customizer/share-design';
import * as html from 'smg/html';
import Modal from 'smg/modal';
import * as toast from 'smg/toast';
import * as uploads from 'smg/uploads';
import { DollarFormat } from 'smg/util';
import { UUID } from 'smg/uuid';
import * as Swiper from 'swiper';
import { Relref } from '../../hugo';
import { BackgroundColorUI, BackgroundColorUIState } from '../../no-minimum-custom-coins/background-color-ui';
import { CoinDesignPersistence } from '../../no-minimum-custom-coins/coin-design-persistence';
import { CoinTextLayer } from '../../no-minimum-custom-coins/coin-text-layer';
import { ImageState, ImageUploadUI } from '../../no-minimum-custom-coins/image-upload-ui';
import { DEFAULT_BOTTOM_TEXT, DEFAULT_TOP_TEXT, RING_INNER, RING_OUTER, TextCustomizerUI, TextState } from '../../no-minimum-custom-coins/text-customizer-ui';
import { TextDropUI } from '../../no-minimum-custom-coins/text-drop-ui';
import { BindTourHelpButtons, StartCoinBuilderTour } from '../../no-minimum-custom-coins/tour';
import { SetupBuilderAddToCart } from '../../product-customizer/add-to-cart';
import { DEFAULT_FONT_FAMILY } from '../../product-customizer/fonts';
import { SetupPreviousDesigns } from '../../product-customizer/previous-designs-ui';
import { BindResetDesignButtons, BindShareDesignButtons } from '../../product-customizer/share-design-ui';
import { ConfirmSpellCheck } from '../../spell-check-modal';

const $bgColor = document.getElementById('bg-color-swatch') as HTMLInputElement | null;
const $mobileBgOverlay = document.getElementById('mobile-bg-color-overlay') as HTMLInputElement | null;
const $bgCircle = document.getElementById('design-bg-circle');
const $lgBgGraphic = document.getElementById('lg-bg-ring-graphic');
const $bgToggle = document.getElementById('bg-color-switch') as HTMLInputElement | null;
const $bgRemove = document.getElementById('design-bg-remove');
const $lgBgRemove = document.getElementById('lg-bg-ring-remove');

let editingCartItemID = '';

async function main() {
    // Canvases start hidden via CSS (opacity: 0) to prevent flash of full-size content
    const $frontCanvas = document.getElementById('front-canvas');
    const $backCanvas = document.getElementById('back-canvas');
    $backCanvas?.classList.add('d-none');

    const $canvasParent = $frontCanvas?.parentElement;
    let $loadingOverlay: HTMLElement | null = null;
    if ($canvasParent) {
        $loadingOverlay = html.FromString(`
            <div class="coin-loading-overlay">
                <div class="spinner-border text-secondary" role="status">
                    <span class="visually-hidden">Loading...</span>
                </div>
            </div>
        `).firstElementChild as HTMLElement;
        $canvasParent.appendChild($loadingOverlay);
    }

    // Background image is 969x969 per coin side
    // Coin content is 824x824 centered (72px padding), export at 2032px (43mm @ 1200 DPI)
    const printCrop = { x: 72, y: 72, width: 824, height: 824 };
    const printConfig = {
        exportScale: 2032 / printCrop.width,
        printCrop,
        printDpi: 1200,
    };

    const frontEngine = new CustomizerEngine({
        container: 'front-canvas',
        width: 969,
        height: 969,
        ...printConfig,
    });

    const backEngine = new CustomizerEngine({
        container: 'back-canvas',
        width: 969,
        height: 969,
        ...printConfig,
    });

    await setupEngine(frontEngine, 'front');
    await setupEngine(backEngine, 'back');

    setupPlatingSelection(frontEngine, backEngine);

    const imageUploadUI = new ImageUploadUI(frontEngine, backEngine);
    const textCustomizerUI = new TextCustomizerUI(frontEngine, backEngine);
    const backgroundColorUI = new BackgroundColorUI(frontEngine, backEngine);

    // Text drop (free-position text blocks) — blocks show as rows in the design layer lists
    const textDropUI = new TextDropUI(frontEngine, backEngine);
    imageUploadUI.setTextDropUI(textDropUI);

    // Selections are exclusive across the two owners
    textDropUI.onSelect = () => imageUploadUI.deselectAll();
    imageUploadUI.onLayerSelected = () => textDropUI.deselectAll();

    document.querySelectorAll<HTMLButtonElement>('.add-text-drop-btn').forEach($btn => {
        $btn.addEventListener('click', e => {
            // The upload-zone instance sits inside the zone's own click-to-upload target
            e.stopPropagation();
            void textDropUI.addBlock();
        });
    });

    const qrModal = new QRModal({
        onPlace: async blob => {
            await imageUploadUI.addBlob(blob, `QR-${UUID()}.png`);
        },
    });
    for (const $btn of document.querySelectorAll<HTMLButtonElement>('.generate-qr-btn')) {
        $btn.addEventListener('click', () => qrModal.open());
    }

    setupSideSelection(frontEngine, backEngine, imageUploadUI, backgroundColorUI);

    frontEngine.fitStageIntoParentContainer();

    setupResponsive(frontEngine, backEngine);

    const $lgGallery = document.querySelector<HTMLElement>('.lg-gallery-swiper');
    if ($lgGallery) {
        new Swiper.Swiper($lgGallery, {
            slidesPerView: 'auto',
            spaceBetween: 16,
            grabCursor: true,
        });
    }

    // autoRestore disabled — restore after UI is fully initialized
    const persistence = new CoinDesignPersistence(frontEngine, backEngine, textCustomizerUI, imageUploadUI, backgroundColorUI, textDropUI);

    const shareDesign = new BuilderShareDesign<DesignState>({
        storageKey: 'coin-share-cache',
        persistence,
        createRequest: async state => {
            // Rendered front preview so the design shows a thumbnail in
            // previous-designs lists — the design still saves without one
            let previewUploadID = 0;
            try {
                const [preview] = await uploads.NewFromURL([{
                    name: 'design-preview.png',
                    mime: 'image/png',
                    url: frontEngine.exportAsDataURL('image/png', undefined, 1024),
                }]);
                previewUploadID = preview.uploadID;
            } catch (err) {
                console.error(err);
            }
            return new models.BuilderDesignCreateRequest({
                uploadID: previewUploadID,
                uploadIDs: imageUploadUI.getState().map(s => s.uploadID).filter(id => id > 0),
                uploadLineItem: {
                    name: 'Custom Challenge Coins',
                    lineItemID: models.LineItemID.LINE_ITEMS_CHALLENGE_COIN,
                    networkSkuid: models.NetworkSKUID.NETWORK_SK_US_NO_MINIMUM_CUSTOM_COINS,
                },
                designData: state,
            });
        },
    });

    // ?cartItem, ?order+?orderLineItem, and ?design win over localStorage.
    // Precedence: cartItem > orderLineItem > design.
    const { restored, editingCartItemID: editingID } = await shareDesign.restoreDesign();
    editingCartItemID = editingID;

    // Must attach AFTER restore so deserialize's significantChange doesn't nuke the cache.
    [frontEngine, backEngine].forEach(engine => {
        engine.on('significantChange', shareDesign.clearCache);
    });
    BindShareDesignButtons(shareDesign);

    // Non-blocking: the previous-designs section fills in below the builder
    void SetupPreviousDesigns({ lineItemID: models.LineItemID.LINE_ITEMS_CHALLENGE_COIN });

    // Reveal canvas after restore — ensures saved uploads load before showing
    requestAnimationFrame(() => {
        requestAnimationFrame(() => {
            setTimeout(() => {
                $frontCanvas?.style.setProperty('opacity', '1');
                if ($loadingOverlay) {
                    $loadingOverlay.style.opacity = '0';
                    setTimeout(() => $loadingOverlay?.remove(), 200);
                }
            }, 0);
        });
    });

    if (!restored) {
        // Fresh design: load the default faces now. Restored designs get their
        // saved faces from restoreState instead — applying defaults here too
        // would race it and could clobber the restored font.
        await textCustomizerUI.applyInitialFaces();
        await textCustomizerUI.setDefaultTextOnLayers();
    }

    SetupBuilderKeyboardShortcuts({
        owners: [
            {
                selectedId: () => imageUploadUI.getSelectedLayerId(),
                selectedNode: () => imageUploadUI.getSelectedNode(),
                deleteSelected: id => imageUploadUI.deleteLayerWithUndo(id),
            },
            {
                selectedId: () => textDropUI.getSelectedId(),
                selectedNode: () => textDropUI.getSelectedNode(),
                deleteSelected: id => textDropUI.deleteBlock(id),
            },
        ],
        engine: () => imageUploadUI.currentEngine(),
        // Delete/nudge bypass the pointer lock — freeze them while a process runs
        enabled: () => !imageUploadUI.isProcessing(),
    });

    setupOppositeSidePreview(frontEngine, backEngine);
    setupAddToCart(frontEngine, backEngine, imageUploadUI, textCustomizerUI, textDropUI, persistence, shareDesign);

    BindTourHelpButtons('.coin-builder-tour-btn');
    // Auto-start the tour once the main-thread init burst has settled. The
    // tour itself no-ops if the user has already seen every step.
    const startTour = () => StartCoinBuilderTour();
    if ('requestIdleCallback' in window) {
        window.requestIdleCallback(startTour, { timeout: 1500 });
    } else {
        setTimeout(startTour, 600);
    }

    BindResetDesignButtons(shareDesign);

    const copyDesign = (from: 'front' | 'back') => {
        const to: 'front' | 'back' = from === 'front' ? 'back' : 'front';
        const fromLabel = from === 'front' ? 'Front' : 'Back';
        const toLabel = to === 'front' ? 'Front' : 'Back';
        new Modal({
            title: `Copy ${fromLabel} Design to ${toLabel}?`,
            body: `This will overwrite your current ${to} side design with the ${from} side design.`,
            size: 'modal-sm',
            confirmText: 'Yes, Copy',
            cancelText: 'Cancel',
            confirmButtonClass: 'btn btn-primary btn-lg col-12 col-md-auto flex-grow-1',
            cancelButtonClass: 'btn btn-outline-secondary btn-lg col-12 col-md-auto flex-grow-1',
            onSubmit: async () => {
                // Snapshot both sides so the whole copy registers as ONE undo/redo step
                const beforeText = structuredClone(textCustomizerUI.getState());
                const beforeBg = backgroundColorUI.getState();
                const beforeImages = imageUploadUI.getState();

                // Copying makes both sides identical to the source side
                const afterText: TextState = {
                    front: structuredClone(beforeText[from]),
                    back: structuredClone(beforeText[from]),
                };
                const afterBg: BackgroundColorUIState = {
                    front: { ...beforeBg[from] },
                    back: { ...beforeBg[from] },
                };
                const sourceImages = beforeImages.filter(img => img.side === from);
                const afterImages = [...sourceImages, ...sourceImages.map(img => ({ ...img, side: to }))];

                const applyState = async (text: TextState, bg: BackgroundColorUIState, images: ImageState[]) => {
                    // restoreState keeps some nested refs (separator customChars) — hand it
                    // a clone so later edits can't corrupt the captured snapshots
                    await textCustomizerUI.restoreState(structuredClone(text));
                    backgroundColorUI.restoreState(bg);
                    imageUploadUI.clearAll(false);
                    await imageUploadUI.restoreState(images);

                    // Land the view on the side the copy overwrote. A real side switch
                    // (radio change) already refreshes every side-scoped control; when the
                    // user is still on that side, refresh those controls directly.
                    const $toRadio = document.querySelector<HTMLInputElement>(`input[name="front-back-radio"][value="${to}"]`);
                    if ($toRadio && !$toRadio.checked) {
                        // The radio change handler switches canvases and side-scoped UIs,
                        // but the toggle switch and segmented buttons only sync themselves
                        // on their own clicks
                        const $sideToggle = document.getElementById('coin-side-toggle-switch') as HTMLInputElement | null;
                        if ($sideToggle) {
                            $sideToggle.checked = to === 'back';
                        }
                        document.querySelectorAll<HTMLButtonElement>('.segmented-btn').forEach($btn => {
                            $btn.classList.toggle('active', $btn.dataset.side === to);
                        });
                        $toRadio.checked = true;
                        $toRadio.dispatchEvent(new Event('change'));
                    } else {
                        textCustomizerUI.setSide(to);
                        backgroundColorUI.setSide(to);
                        imageUploadUI.setSide(to);
                        syncColorGraphics();
                    }

                    void persistence.save();
                };

                await applyState(afterText, afterBg, afterImages);

                const toEngine = to === 'front' ? frontEngine : backEngine;
                toEngine.addAction({
                    type: 'COPY_DESIGN',
                    description: `Copy ${from} design to ${to}`,
                    undo: () => {
                        void applyState(beforeText, beforeBg, beforeImages);
                    },
                    redo: () => {
                        void applyState(afterText, afterBg, afterImages);
                    },
                });
                toast.Undo(`Copied ${from} design to ${to}`, () => {
                    toEngine.undoLocal();
                });
            },
        });
    };
    document.getElementById('copy-front-btn')?.addEventListener('click', () => copyDesign('front'));
    document.getElementById('copy-front-btn-mobile')?.addEventListener('click', () => copyDesign('front'));
    document.getElementById('copy-back-btn')?.addEventListener('click', () => copyDesign('back'));
    document.getElementById('copy-back-btn-mobile')?.addEventListener('click', () => copyDesign('back'));

    // =========================================================================
    // UI wiring
    // =========================================================================

    const $sideToggle = document.getElementById('coin-side-toggle-switch') as HTMLInputElement | null;
    $sideToggle?.addEventListener('change', () => {
        const side = $sideToggle.checked ? 'back' : 'front';
        const $radio = document.querySelector<HTMLInputElement>(`input[name="front-back-radio"][value="${side}"]`);
        if ($radio) {
            $radio.checked = true;
            $radio.dispatchEvent(new Event('change'));
        }
    });

    const $segmentedBtns = document.querySelectorAll<HTMLButtonElement>('.segmented-btn');
    $segmentedBtns.forEach($btn => {
        $btn.addEventListener('click', () => {
            const side = $btn.dataset.side;
            if (!side) {
                return;
            }

            $segmentedBtns.forEach($b => $b.classList.remove('active'));
            $btn.classList.add('active');

            if ($sideToggle) {
                $sideToggle.checked = side === 'back';
            }

            const $radio = document.querySelector<HTMLInputElement>(`input[name="front-back-radio"][value="${side}"]`);
            if ($radio) {
                $radio.checked = true;
                $radio.dispatchEvent(new Event('change'));
            }
        });
    });

    // Sync segmented toggle when side changes from other sources (toggle, restore)
    document.querySelectorAll<HTMLInputElement>('input[name="front-back-radio"]').forEach($radio => {
        $radio.addEventListener('change', () => {
            if ($radio.checked) {
                $segmentedBtns.forEach($btn => {
                    $btn.classList.toggle('active', $btn.dataset.side === $radio.value);
                });
            }
        });
    });

    // Wire Text/Design tab bar
    // Mobile: data-active-tab attribute drives CSS visibility (scoped to < 992px)
    // LG+: .show class on #design-tab drives CSS visibility (scoped to >= 992px)
    // Both are set simultaneously — they don't conflict across breakpoints.
    const $root = document.getElementById('no-minimum-custom-coins');
    const $tabBarItems = document.querySelectorAll<HTMLButtonElement>('.tab-bar-item');
    const $designTab = document.getElementById('design-tab');
    const $textTab = document.getElementById('text-tab');

    const switchTab = (tab: string) => {
        $tabBarItems.forEach($t => {
            $t.classList.toggle('active', $t.dataset.tab === tab);
        });
        if (tab === 'design') {
            $root?.setAttribute('data-active-tab', 'design');
            $designTab?.classList.add('show', 'active');
            $textTab?.classList.remove('show', 'active');
        } else {
            $root?.removeAttribute('data-active-tab');
            $textTab?.classList.add('show', 'active');
            $designTab?.classList.remove('show', 'active');
        }
    };

    $tabBarItems.forEach($tab => {
        $tab.addEventListener('click', () => {
            switchTab($tab.dataset.tab ?? 'text');
        });
    });

    setupArchTextClick(frontEngine, backEngine, textCustomizerUI, switchTab);

    const $uploadInput = document.getElementById('upload-input') as HTMLInputElement | null;
    const triggerUpload = () => $uploadInput?.click();

    document.querySelector<HTMLButtonElement>('.add-images-icon-box')?.addEventListener('click', triggerUpload);
    document.getElementById('mobile-add-image-btn')?.addEventListener('click', triggerUpload);
    document.getElementById('upload-zone')?.addEventListener('click', triggerUpload);
    document.getElementById('lg-upload-zone')?.addEventListener('click', triggerUpload);

    const $coinUploadCentered = document.getElementById('coin-upload-centered-btn');
    const $coinUploadCornerLg = document.getElementById('coin-upload-corner-btn-lg');
    const $coinUploadCornerMobile = document.getElementById('coin-upload-corner-btn-mobile');
    const handleUploadClick = () => {
        switchTab('design');
        setTimeout(triggerUpload, 100);
    };
    $coinUploadCentered?.addEventListener('click', handleUploadClick);
    $coinUploadCornerLg?.addEventListener('click', handleUploadClick);
    $coinUploadCornerMobile?.addEventListener('click', handleUploadClick);

    const $textTabUploadCta = document.getElementById('text-tab-upload-cta');
    $textTabUploadCta?.addEventListener('click', () => {
        switchTab('design');
        const $mobileTabBar = document.getElementById('text-design-tab-bar');
        if ($mobileTabBar) {
            const stickyHeight = document.getElementById('coin-preview-section')?.offsetHeight ?? 0;
            const targetTop = $mobileTabBar.getBoundingClientRect().top + window.scrollY - stickyHeight;
            window.scrollTo({ top: targetTop, behavior: 'smooth' });
        }
        setTimeout(triggerUpload, 500);
    });

    document.addEventListener('coin-uploads-changed', () => {
        const hasAny = imageUploadUI.hasUploads('front') || imageUploadUI.hasUploads('back');
        $textTabUploadCta?.classList.toggle('d-none', hasAny);
    });

    const updateUploadButtons = () => {
        const $checked = document.querySelector<HTMLInputElement>('input[name="front-back-radio"]:checked');
        const side: 'front' | 'back' = $checked?.value === 'back' ? 'back' : 'front';
        // Text blocks count as content too — the centered Upload CTA shouldn't cover them
        const hasOnSide = imageUploadUI.hasUploads(side) || textDropUI.hasBlocks(side);
        $coinUploadCentered?.classList.toggle('d-none', hasOnSide);
        $coinUploadCornerLg?.classList.toggle('d-none', !hasOnSide);
        $coinUploadCornerMobile?.classList.toggle('d-none', !hasOnSide);

        // The header's right-side stack uses fixed CSS slots (Upload row 1,
        // copy row 2) — no per-state shuffling
    };
    document.addEventListener('coin-uploads-changed', updateUploadButtons);
    document.addEventListener('coin-text-drop-changed', updateUploadButtons);
    document.querySelectorAll<HTMLInputElement>('input[name="front-back-radio"]').forEach($radio => {
        $radio.addEventListener('change', updateUploadButtons);
    });
    updateUploadButtons();

    const $quantity = document.getElementById('quantity') as HTMLInputElement | null;
    const $lgQuantity = document.getElementById('lg-quantity') as HTMLInputElement | null;
    const $priceAmount = document.querySelector('.coin-price-amount');
    const $lgPriceAmount = document.querySelector('.lg-price-amount');

    async function updatePrice(quantity: number) {
        const qty = Math.max(1, quantity);
        const unitPrice = await GetPrice(
            'challenge coins',
            'Made in USA - Simulated Enamel Coin',
            '2"',
            qty,
        );

        const $checkedPlating = document.querySelector<HTMLInputElement>('input[name="plating-radio"]:checked');
        const plating = $checkedPlating?.value ?? 'antique brass';

        let optionsPrice = new Big(0);
        optionsPrice = optionsPrice.add(await GetOptionPrice('challenge coins', 'plating', plating));
        optionsPrice = optionsPrice.add(await GetOptionPrice('challenge coins', 'edge', 'rope'));

        const formatted = DollarFormat(unitPrice.add(optionsPrice));
        if ($priceAmount) {
            $priceAmount.textContent = formatted;
        }
        if ($lgPriceAmount) {
            $lgPriceAmount.textContent = formatted;
        }
    }

    $quantity?.addEventListener('input', () => {
        const val = parseInt($quantity.value, 10) || 1;
        if ($lgQuantity) {
            $lgQuantity.value = String(val);
        }
        void updatePrice(val);
    });

    $lgQuantity?.addEventListener('input', () => {
        const val = parseInt($lgQuantity.value, 10) || 1;
        if ($quantity) {
            $quantity.value = String(val);
        }
        void updatePrice(val);
    });

    void updatePrice(1);

    async function updatePricingTiers() {
        const sizes = await GetSizes('challenge coins', 'Made in USA - Simulated Enamel Coin');
        const coinSize = sizes.find(s => s.networkName() === '2"' || s.name === '2"');
        if (!coinSize) {
            return;
        }

        const $checkedPlating = document.querySelector<HTMLInputElement>('input[name="plating-radio"]:checked');
        const plating = $checkedPlating?.value ?? 'antique brass';

        let optionsPrice = new Big(0);
        optionsPrice = optionsPrice.add(await GetOptionPrice('challenge coins', 'plating', plating));
        optionsPrice = optionsPrice.add(await GetOptionPrice('challenge coins', 'edge', 'rope'));

        const prices = coinSize.networkLineItemTypeSize?.prices ?? [];
        const tiers: string[] = [];
        for (let i = 0; i < prices.length; i++) {
            const p = prices[i];
            const nextQty = i < prices.length - 1 ? prices[i + 1].quantity : undefined;
            if (nextQty && nextQty <= 25) {
                tiers.push(`<li class="pb-1">${p.quantity}-${nextQty - 1} coins for <span class="text-secondary fw-bold">${DollarFormat(p.price.add(optionsPrice))} each</span></li>`);
            } else {
                tiers.push(`<li class="pb-1">${p.quantity}+ coins for <span class="text-secondary fw-bold">${DollarFormat(p.price.add(optionsPrice))} each</span></li>`);
                break;
            }
        }

        const $mobile = document.getElementById('pricing-tiers-mobile');
        if ($mobile) {
            $mobile.innerHTML = tiers.join('');
        }

        const $desktop = document.getElementById('pricing-tiers-desktop');
        if ($desktop) {
            const mid = Math.ceil(tiers.length / 2);
            $desktop.innerHTML = `<div>${tiers.slice(0, mid).join('')}</div><div>${tiers.slice(mid).join('')}</div>`;
        }
    }

    void updatePricingTiers();

    const $lgAddToCart = document.getElementById('lg-add-to-cart');
    const $addToCartMobile = document.getElementById('add-to-cart');
    $lgAddToCart?.addEventListener('click', () => {
        if ($lgQuantity && $quantity) {
            $quantity.value = $lgQuantity.value;
        }
        $addToCartMobile?.click();
    });

    setupRingColorCards();
    syncColorGraphics();

    document.querySelectorAll<HTMLInputElement>('input[name="front-back-radio"]').forEach($radio => {
        $radio.addEventListener('change', () => {
            syncColorGraphics();
        });
    });

    setupPlatingCards();
    setupDesignBgCard();
    setupRingModeRadios();
    setupTextSizeButtons(textCustomizerUI);
    setupTextColorIndicators(textCustomizerUI);

    for (const engine of [frontEngine, backEngine]) {
        engine.on('layerUpdated', () => updateTextSizeIndicators(textCustomizerUI));
    }
    setupMetalColorToggle();

    // Translucent ring toggle — syncs both mobile and LG checkboxes
    const $translucentToggles = document.querySelectorAll<HTMLInputElement>('#ring-translucent, #lg-ring-translucent');
    $translucentToggles.forEach($toggle => {
        $toggle.addEventListener('change', () => {
            // Sync all toggles to same state
            $translucentToggles.forEach($t => {
                $t.checked = $toggle.checked;
            });
            textCustomizerUI.setRingTranslucent($toggle.checked);
        });
    });

    document.querySelectorAll('#customization-card [data-bs-toggle="tooltip"]').forEach(el => {
        new bootstrap.Tooltip(el);
    });

    document.querySelectorAll<HTMLInputElement>('input[name="plating-radio"]').forEach($radio => {
        $radio.addEventListener('change', () => {
            const quantity = parseInt($quantity?.value ?? '1', 10) || 1;
            void updatePrice(quantity);
            void updatePricingTiers();
        });
    });
}

async function setupEngine(engine: CustomizerEngine, side: 'front' | 'back') {
    const bg = new ImageLayer(`${side}-bg`, {
        userEditable: false,
        isBackground: true,
    });
    await bg.setImage(`${Relref('/')}img/no-minimum-custom-coins/antique brass bg-fs8.png`);
    await engine.addLayer(`${side}-bg`, bg);

    // The background image is 2000x968 with both coins side by side
    // Front coin: x=0, Back coin: x=1032 (968 + 64 gap)
    // Offset the back side's background to show the right portion
    const backSideOffsetX = -1032;
    if (side === 'back') {
        bg.setPosition(backSideOffsetX, 0);
    }

    // Define image zone with mask for user uploads
    // The visible circular mask area is 645x645 (66% of 969x969 canvas), centered
    // Center of 969x969 is ~484, so bounds start at (162, 162) for a 645x645 area
    engine.defineImageZone({
        id: 'coin-center',
        maskUrl: `${Relref('/')}img/no-minimum-custom-coins/front-mask.png`,
        bounds: { x: 162, y: 162, width: 645, height: 645 },
        maskPosition: side === 'back' ? { x: 0, y: 0 } : undefined,
    });

    // Canvas is 969x969, center at (484.5, 484.5)
    // Inner circle radius = 322.5 (645/2), outer edge = 484.5
    // Ring midpoint would be ~403, but visually centering requires smaller radius
    // to account for text height extending outward
    const canvasCenter = { x: 484.5, y: 484.5 };
    const textRadius = 365;

    const topText = new CoinTextLayer(`${side}-top-text`, {
        font: {
            family: DEFAULT_FONT_FAMILY,
            weight: 'bold',
            size: 11, // pt - converted to px by CoinTextLayer
            color: '#000000',
        },
        radius: textRadius,
        center: canvasCenter,
        position: 'top',
    });
    await engine.addLayer(`${side}-top-text`, topText);

    const bottomText = new CoinTextLayer(`${side}-bottom-text`, {
        font: {
            family: DEFAULT_FONT_FAMILY,
            weight: 'bold',
            size: 11, // pt - converted to px by CoinTextLayer
            color: '#000000',
        },
        radius: textRadius,
        center: canvasCenter,
        position: 'bottom',
    });
    await engine.addLayer(`${side}-bottom-text`, bottomText);
}

const platingBackgrounds: Record<string, string> = {
    'antique brass': 'antique brass bg-fs8.png',
    'antique silver': 'antique silver bg-fs8.png',
};

function setupPlatingSelection(front: CustomizerEngine, back: CustomizerEngine) {
    const $platingRadios = document.querySelectorAll<HTMLInputElement>('input[name="plating-radio"]');

    $platingRadios.forEach($radio => {
        $radio.addEventListener('change', async () => {
            if (!$radio.checked) {
                return;
            }

            const plating = $radio.value;
            const bgFilename = platingBackgrounds[plating];
            if (!bgFilename) {
                return;
            }

            // Sync emit: afterRestore re-dispatches this change event, and this must
            // fire before the share-dirty listener attaches post-restore
            front.emit('significantChange', { layerId: 'plating' });

            const bgUrl = `${Relref('/')}img/no-minimum-custom-coins/${bgFilename}`;

            const frontBg = front.getLayer('front-bg') as ImageLayer | undefined;
            const backBg = back.getLayer('back-bg') as ImageLayer | undefined;

            await Promise.all([
                frontBg?.setImage(bgUrl),
                backBg?.setImage(bgUrl),
            ]);

            // Reposition back background to show right portion
            const backSideOffsetX = -1032;
            backBg?.setPosition(backSideOffsetX, 0);
        });
    });
}

function setupSideSelection(front: CustomizerEngine, back: CustomizerEngine, imageUploadUI: ImageUploadUI, backgroundColorUI: BackgroundColorUI) {
    const $frontCanvas = document.getElementById('front-canvas');
    const $backCanvas = document.getElementById('back-canvas');
    const $sideRadios = document.querySelectorAll<HTMLInputElement>('input[name="front-back-radio"]');

    let activeSide: 'front' | 'back' = 'front';

    /** Snap every side control back to the side that's actually shown */
    function syncSideControls(side: 'front' | 'back') {
        $sideRadios.forEach($radio => {
            $radio.checked = $radio.value === side;
        });
        const $sideToggle = document.getElementById('coin-side-toggle-switch') as HTMLInputElement | null;
        if ($sideToggle) {
            $sideToggle.checked = side === 'back';
        }
        document.querySelectorAll<HTMLButtonElement>('.segmented-btn').forEach($btn => {
            $btn.classList.toggle('active', $btn.dataset.side === side);
        });
    }

    function showSide(side: 'front' | 'back') {
        activeSide = side;
        const isFront = side === 'front';
        const $incomingCanvas = isFront ? $frontCanvas : $backCanvas;
        const $outgoingCanvas = isFront ? $backCanvas : $frontCanvas;
        const incomingEngine = isFront ? front : back;

        $outgoingCanvas?.style.setProperty('opacity', '0');
        $incomingCanvas?.style.setProperty('opacity', '0');

        $frontCanvas?.classList.toggle('d-none', !isFront);
        $backCanvas?.classList.toggle('d-none', isFront);
        front.setActive(isFront);
        back.setActive(!isFront);

        incomingEngine.fitStageIntoParentContainer();
        imageUploadUI.setSide(side);
        backgroundColorUI.setSide(side);

        const $copyFront = document.getElementById('copy-front-btn');
        const $copyBack = document.getElementById('copy-back-btn');
        $copyFront?.classList.toggle('d-none', isFront);
        document.getElementById('copy-front-btn-mobile')?.classList.toggle('d-none', isFront);
        $copyBack?.classList.toggle('d-none', !isFront);
        document.getElementById('copy-back-btn-mobile')?.classList.toggle('d-none', !isFront);

        // Double RAF + setTimeout ensures paint cycles complete before reveal
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                setTimeout(() => {
                    $incomingCanvas?.style.setProperty('opacity', '1');
                }, 0);
            });
        });
    }

    function resetSideToFront() {
        const $frontRadio = Array.from($sideRadios).find($radio => $radio.value === 'front');

        $sideRadios.forEach($radio => {
            $radio.checked = $radio.value === 'front';
        });

        const $sideToggle = document.getElementById('coin-side-toggle-switch') as HTMLInputElement | null;
        if ($sideToggle) {
            $sideToggle.checked = false;
        }

        if ($frontRadio) {
            $frontRadio.dispatchEvent(new Event('change'));
        } else {
            showSide('front');
        }
    }

    $sideRadios.forEach($radio => {
        $radio.addEventListener('change', () => {
            if (!$radio.checked) {
                return;
            }
            if (imageUploadUI.isProcessing()) {
                syncSideControls(activeSide);
                return;
            }
            showSide($radio.value === 'back' ? 'back' : 'front');
        });
    });

    resetSideToFront();

    window.addEventListener('pageshow', event => {
        if (event.persisted) {
            resetSideToFront();
        }
    });
}

// Clicking the rendered arch text on the preview opens its editor: activate the
// Text tab and focus the matching input (#9950). The text is canvas content
// (TextPath rasterized to a full-canvas image in Color mode, ring cutouts in
// Metal mode), so the hit is resolved geometrically: within the ring band, a
// click counts only when it lands on an arch's rendered text span — long text
// wraps well past the horizontal midline and still belongs to its own input;
// empty ring space is not a target.
function setupArchTextClick(
    frontEngine: CustomizerEngine,
    backEngine: CustomizerEngine,
    textCustomizerUI: TextCustomizerUI,
    switchTab: (tab: string) => void,
) {
    const $topInput = document.getElementById('top-text-input') as HTMLInputElement | null;
    const $bottomInput = document.getElementById('bottom-text-input') as HTMLInputElement | null;
    if (!$topInput || !$bottomInput) {
        return;
    }

    const CANVAS_CENTER = 969 / 2;
    const SPAN_CACHE_MS = 200;
    const spanCache = new WeakMap<CoinTextLayer, { at: number; value: number }>();

    /** Which arch the pointer is over, or null when the click isn't an arch-text
     * hit (ring off, outside the ring band, or the click belongs to a text block
     * or image). */
    const archAt = (engine: CustomizerEngine, side: 'front' | 'back', target: unknown): 'top' | 'bottom' | null => {
        // Ring off → no arch text is rendered at all
        if (!textCustomizerUI.getState()[side].ring.wantsRing) {
            return null;
        }
        // Free-position text blocks are real Konva text nodes with their own
        // click-to-select handling
        if (target instanceof Konva.Text || target instanceof Konva.TextPath) {
            return null;
        }
        const stage = engine.getStage();
        const pointer = stage.getPointerPosition();
        if (!pointer) {
            return null;
        }
        // An image under the pointer keeps the click — image selection wins,
        // same as before this affordance existed
        if (engine.getTopmostImageLayerAt(pointer.x, pointer.y)) {
            return null;
        }
        const scale = stage.scaleX() || 1;
        const dx = pointer.x / scale - CANVAS_CENTER;
        const dy = pointer.y / scale - CANVAS_CENTER;
        const radius = Math.hypot(dx, dy);
        if (radius < RING_INNER || radius > RING_OUTER) {
            return null;
        }

        // Each arch renders centered at 12 o'clock (top) / 6 o'clock (bottom),
        // spanning its text length as a fraction of the circle — the click only
        // counts when it lands on an arch's rendered span, not on empty ring
        // space, and a long wrapping text owns its whole span regardless of
        // which half of the ring it reaches into.
        const clickAngle = Math.atan2(dy, dx);
        const angularDistance = (fromCenter: number): number => {
            const d = Math.abs(clickAngle - fromCenter);
            return d > Math.PI ? 2 * Math.PI - d : d;
        };
        const distFromTopCenter = angularDistance(-Math.PI / 2);
        const distFromBottomCenter = angularDistance(Math.PI / 2);

        const halfSpan = (layer: CoinTextLayer | undefined): number => {
            if (!layer?.getText().trim()) {
                return 0;
            }
            // Measuring forces SVG layout, and this also runs on mousemove for
            // the hover cursor — a briefly stale span is imperceptible there
            const cached = spanCache.get(layer);
            const now = performance.now();
            if (cached && now - cached.at < SPAN_CACHE_MS) {
                return cached.value;
            }
            const value = (layer.getTextLengthPercent() / 100) * Math.PI;
            spanCache.set(layer, { at: now, value });
            return value;
        };
        const topLayer = engine.getLayer(`${side}-top-text`) as CoinTextLayer | undefined;
        const bottomLayer = engine.getLayer(`${side}-bottom-text`) as CoinTextLayer | undefined;

        const onTopSpan = distFromTopCenter <= halfSpan(topLayer);
        const onBottomSpan = distFromBottomCenter <= halfSpan(bottomLayer);
        if (onTopSpan && onBottomSpan) {
            // Overlapping spans — the arch whose text is centered nearer owns it
            return distFromTopCenter <= distFromBottomCenter ? 'top' : 'bottom';
        }
        if (onTopSpan) {
            return 'top';
        }
        if (onBottomSpan) {
            return 'bottom';
        }
        return null;
    };

    const focusArchInput = (which: 'top' | 'bottom') => {
        switchTab('text');
        const $input = which === 'top' ? $topInput : $bottomInput;
        $input.focus({ preventScroll: true });
        $input.select();

        // On mobile the sticky preview section covers the top of the viewport,
        // so the browser's own focus scroll can leave the input hidden under it
        const $section = document.getElementById('coin-preview-section');
        const stickyHeight = $section && getComputedStyle($section).position === 'sticky' ? $section.offsetHeight : 0;
        const rect = $input.getBoundingClientRect();
        if (rect.top < stickyHeight || rect.bottom > window.innerHeight) {
            window.scrollTo({ top: rect.top + window.scrollY - stickyHeight - 16, behavior: 'smooth' });
        }
    };

    for (const [engine, side] of [[frontEngine, 'front'], [backEngine, 'back']] as const) {
        const stage = engine.getStage();
        stage.on('click tap', e => {
            const arch = archAt(engine, side, e.target);
            if (arch) {
                focusArchInput(arch);
            }
        });
        stage.on('mousemove', e => {
            const $container = stage.container();
            // Only ever toggle between '' and 'pointer' so another feature's
            // cursor (the background eyedropper) is never clobbered
            if ($container.style.cursor !== '' && $container.style.cursor !== 'pointer') {
                return;
            }
            $container.style.cursor = archAt(engine, side, e.target) ? 'pointer' : '';
        });
    }
}

function setupOppositeSidePreview(front: CustomizerEngine, back: CustomizerEngine) {
    const $img = document.getElementById('opposite-side-img') as HTMLImageElement | null;
    const $label = document.getElementById('opposite-side-label');
    const $preview = document.getElementById('opposite-side-preview');
    const $sideRadios = document.querySelectorAll<HTMLInputElement>('input[name="front-back-radio"]');

    if (!$img || !$preview) {
        return;
    }

    const img = $img;
    const preview = $preview;
    let currentSide: 'front' | 'back' = 'front';
    let updateTimer: ReturnType<typeof setTimeout> | null = null;

    function updatePreview() {
        const engine = currentSide === 'front' ? back : front;
        img.src = engine.exportAsDataURL('image/png', undefined, 1024);
        if ($label) {
            $label.textContent = currentSide === 'front' ? 'Back' : 'Front';
        }
        preview.classList.remove('d-none');
    }

    function scheduleUpdate() {
        if (updateTimer) {
            clearTimeout(updateTimer);
        }
        updateTimer = setTimeout(updatePreview, 150);
    }

    // Update preview when the opposite side changes
    front.on('layerUpdated', () => {
        if (currentSide === 'back') {
            scheduleUpdate();
        }
    });
    back.on('layerUpdated', () => {
        if (currentSide === 'front') {
            scheduleUpdate();
        }
    });

    // Update on side switch
    $sideRadios.forEach($radio => {
        $radio.addEventListener('change', () => {
            if ($radio.checked) {
                currentSide = $radio.value as 'front' | 'back';
                updatePreview();
            }
        });
    });

    // Click to switch sides
    preview.addEventListener('click', () => {
        const newSide = currentSide === 'front' ? 'back' : 'front';
        const $radio = document.querySelector<HTMLInputElement>(`input[name="front-back-radio"][value="${newSide}"]`);
        if ($radio) {
            $radio.checked = true;
            $radio.dispatchEvent(new Event('change'));
        }
    });

    // Initial render
    updatePreview();
}

function setupResponsive(front: CustomizerEngine, back: CustomizerEngine) {
    const $container = document.getElementById('coin-preview-container');
    const $section = document.getElementById('coin-preview-section');

    function fitActiveStage() {
        if (front.isActive()) {
            front.fitStageIntoParentContainer();
        } else if (back.isActive()) {
            back.fitStageIntoParentContainer();
        }
    }

    function handleResize() {
        if ($container) {
            const isSticky = $section && getComputedStyle($section).position === 'sticky';
            if (isSticky) {
                // Below LG: cap the canvas so sticky section doesn't exceed 50vh
                const prevMaxHeight = $container.style.maxHeight;
                $container.style.maxHeight = '0';
                const nonCanvasHeight = $section.offsetHeight;
                $container.style.maxHeight = prevMaxHeight;

                const available = Math.floor(window.innerHeight * 0.5) - nonCanvasHeight;
                const constraint = available > 0 ? `${available}px` : '';
                $container.style.maxHeight = constraint;
                $container.style.maxWidth = constraint; // keep it square
            } else {
                $container.style.maxHeight = '';
                $container.style.maxWidth = '';
            }
        }

        fitActiveStage();
    }

    if ($container) {
        new ResizeObserver(fitActiveStage).observe($container);
    }

    window.addEventListener('resize', handleResize);
    handleResize();
}

function setupAddToCart(
    frontEngine: CustomizerEngine,
    backEngine: CustomizerEngine,
    imageUploadUI: ImageUploadUI,
    textCustomizerUI: TextCustomizerUI,
    textDropUI: TextDropUI,
    persistence: CoinDesignPersistence,
    shareDesign: BuilderShareDesign<DesignState>,
) {
    const $addToCart = document.getElementById('add-to-cart') as HTMLButtonElement | null;
    const $quantity = document.getElementById('quantity') as HTMLInputElement | null;
    const $loadingOverlay = document.getElementById('cart-loading-overlay');

    if (!$addToCart || !$quantity) {
        return;
    }

    /** Each arch only matches its own default — "BOTTOM TEXT" in the top input isn't "default". */
    const isDefaultText = (text: string, expected: string): boolean => text.trim() === expected;

    const sideHasDefaultText = (side: 'front' | 'back'): boolean => {
        // Text ring off → text doesn't render on the coin at all, so the
        // input still holding "TOP TEXT" / "BOTTOM TEXT" is irrelevant.
        if (!textCustomizerUI.getState()[side].ring.wantsRing) {
            return false;
        }
        const engine = side === 'front' ? frontEngine : backEngine;
        const topText = (engine.getLayer(`${side}-top-text`) as CoinTextLayer)?.getText() ?? '';
        const bottomText = (engine.getLayer(`${side}-bottom-text`) as CoinTextLayer)?.getText() ?? '';
        return isDefaultText(topText, DEFAULT_TOP_TEXT) || isDefaultText(bottomText, DEFAULT_BOTTOM_TEXT);
    };

    const hasDefaultText = (): boolean => sideHasDefaultText('front') || sideHasDefaultText('back');

    /** Returns the side that has default text. Front takes priority if both do. */
    const sideWithDefaultText = (): 'front' | 'back' | null => {
        if (sideHasDefaultText('front')) {
            return 'front';
        }
        if (sideHasDefaultText('back')) {
            return 'back';
        }
        return null;
    };

    const switchToSide = (targetSide: 'front' | 'back'): void => {
        const $radio = document.querySelector<HTMLInputElement>(
            `input[name="front-back-radio"][value="${targetSide}"]`,
        );
        if ($radio && !$radio.checked) {
            $radio.checked = true;
            $radio.dispatchEvent(new Event('change'));
        }
    };

    SetupBuilderAddToCart({
        buttons: [$addToCart],
        $quantity,
        $loadingOverlay,
        validateContent: () => {
            if (!imageUploadUI.hasUploads('front') && !textDropUI.hasVisibleContent('front')) {
                return 'Please add an image or text to the front side to add to cart';
            }
            return null;
        },
        // Typed ring text checked exact (no OCR) plus uploaded artwork (OCR'd server-side
        // by upload ID). Front is always present; the back only when it has content.
        // Index 0 = front, 1 = back for navigation.
        spellCheckItems: () => {
            const items: models.ISpellCheckItem[] = [];
            {
                const ringOn = textCustomizerUI.getState().front.ring.wantsRing;
                const top = (frontEngine.getLayer('front-top-text') as CoinTextLayer)?.getText()?.trim() ?? '';
                const bottom = (frontEngine.getLayer('front-bottom-text') as CoinTextLayer)?.getText()?.trim() ?? '';
                const texts = ringOn ? [top, bottom].filter(Boolean) : [];
                texts.push(...textDropUI.visibleBlockTexts('front'));
                items.push({
                    text: texts.join('\n'),
                    uploadIDs: imageUploadUI.getUploadIDsForSide('front'),
                });
            }
            if (imageUploadUI.hasUploads('back') || textDropUI.hasVisibleContent('back') || textCustomizerUI.getState().back.ring.wantsRing) {
                const ringOn = textCustomizerUI.getState().back.ring.wantsRing;
                const top = (backEngine.getLayer('back-top-text') as CoinTextLayer)?.getText()?.trim() ?? '';
                const bottom = (backEngine.getLayer('back-bottom-text') as CoinTextLayer)?.getText()?.trim() ?? '';
                const texts = ringOn ? [top, bottom].filter(Boolean) : [];
                texts.push(...textDropUI.visibleBlockTexts('back'));
                items.push({
                    text: texts.join('\n'),
                    uploadIDs: imageUploadUI.getUploadIDsForSide('back'),
                });
            }
            return items;
        },
        confirmSpellCheck: ConfirmSpellCheck,
        onSpellCheckDeclined: findings => {
            switchToSide(findings.some(f => f.imageIndex === 0) ? 'front' : 'back');
        },
        confirmExtras: () => new Promise<boolean>(resolve => {
            if (!hasDefaultText()) {
                resolve(true);
                return;
            }
            let confirmed = false;
            new Modal({
                title: 'Default text detected',
                text: 'One or more text fields still contain the default placeholder text ' +
                    '(e.g. "TOP TEXT" or "BOTTOM TEXT"). All orders are final — your coin will be printed ' +
                    '<strong>exactly</strong> as shown in the preview. Are you sure you want to continue?',
                size: 'modal-sm',
                confirmText: 'Yes, Add to cart',
                cancelText: 'Go Back & Edit',
                confirmButtonClass: 'btn btn-secondary text-white btn-lg col-12 col-md-auto flex-grow-1',
                cancelButtonClass: 'btn btn-outline-secondary btn-lg col-12 col-md-auto flex-grow-1',
                onSubmit: () => {
                    confirmed = true;
                    resolve(true);
                },
                onClose: () => {
                    if (confirmed) {
                        return;
                    }
                    // User dismissed without confirming — show them the side to fix
                    const targetSide = sideWithDefaultText();
                    if (targetSide) {
                        switchToSide(targetSide);
                    }
                    resolve(false);
                },
            });
        }),
        buildCartItem: async quantity => {
            // Back counts as designed if it has uploads OR printable text blocks
            const hasBackUploads = imageUploadUI.hasUploads('back') || textDropUI.hasVisibleContent('back');

            const frontTopText = (frontEngine.getLayer('front-top-text') as CoinTextLayer)?.getText() ?? '';
            const frontBottomText = (frontEngine.getLayer('front-bottom-text') as CoinTextLayer)?.getText() ?? '';
            const backTopText = (backEngine.getLayer('back-top-text') as CoinTextLayer)?.getText() ?? '';
            const backBottomText = (backEngine.getLayer('back-bottom-text') as CoinTextLayer)?.getText() ?? '';

            const frontUploadIDs = imageUploadUI.getUploadIDsForSide('front');
            const backUploadIDs = hasBackUploads ? imageUploadUI.getUploadIDsForSide('back') : [];

            // Previews capped at 2048 (image-transform xxl) — print files below stay full-res (#8495)
            const frontPreview = frontEngine.exportAsDataURL('image/png', undefined, 2048);
            const backPreview = backEngine.exportAsDataURL('image/png', undefined, 2048);

            const frontSkip = textCustomizerUI.isRingTranslucent('front') ? ['text-ring'] : undefined;
            const backSkip = textCustomizerUI.isRingTranslucent('back') ? ['text-ring'] : undefined;

            const [frontColor, frontWhite, backColor, backWhite] = await Promise.all([
                frontEngine.exportColorLayerForPrint(),
                frontEngine.exportWhiteLayerForPrint(frontSkip),
                hasBackUploads ? backEngine.exportColorLayerForPrint() : Promise.resolve(''),
                hasBackUploads ? backEngine.exportWhiteLayerForPrint(backSkip) : Promise.resolve(''),
            ]);

            const $checkedPlating = document.querySelector<HTMLInputElement>('input[name="plating-radio"]:checked');
            const platingOption = $checkedPlating?.value ?? 'antique brass';

            const previewUploads = await uploads.NewFromURL([
                { name: 'front.png', mime: 'image/png', url: frontPreview },
                { name: 'back.png', mime: 'image/png', url: backPreview },
            ]);

            // "." prefix hides print files from customer. A blank back has no
            // print files at all — empty-URL entries here used to upload the
            // customizer page itself as .back.png/.back-white.png (#9301)
            const printUploads = await uploads.NewFromURL([
                { name: '.front.png', mime: 'image/png', url: frontColor },
                { name: '.front-white.png', mime: 'image/png', url: frontWhite },
                ...hasBackUploads ? [
                    { name: '.back.png', mime: 'image/png', url: backColor },
                    { name: '.back-white.png', mime: 'image/png', url: backWhite },
                ] : [],
            ]);

            const options: { name: string; value: string }[] = [];
            if (!hasBackUploads) {
                options.push({ name: 'Back', value: 'Blank (no design)' });
            }
            const textState = textCustomizerUI.getState();
            if (textState.front.ring.wantsRing && frontTopText) {
                options.push({ name: 'Front Top Text', value: frontTopText });
            }
            if (textState.front.ring.wantsRing && frontBottomText) {
                options.push({ name: 'Front Bottom Text', value: frontBottomText });
            }
            if (hasBackUploads && textState.back.ring.wantsRing && backTopText) {
                options.push({ name: 'Back Top Text', value: backTopText });
            }
            if (hasBackUploads && textState.back.ring.wantsRing && backBottomText) {
                options.push({ name: 'Back Bottom Text', value: backBottomText });
            }

            // Persist the design itself first so the cart item can pin a
            // specific (BuilderDesignID, Version). Reuses the share cache so
            // Share + Add to cart never create duplicate BuilderDesign rows
            // for the same state.
            const design = await shareDesign.ensureDesign();

            // Back preview is added via cartItemUploads (with 'proof' tag), not here
            return {
                uploadID: previewUploads[0].uploadID, // front preview as main image
                quantity,
                options,
                cartItemUploadIDs: [
                    ...frontUploadIDs,          // original front uploads
                    ...backUploadIDs,           // original back uploads
                    ...printUploads.map(u => u.uploadID), // print files
                ],
                cartItemUploads: [
                    { uploadID: previewUploads[1].uploadID, tags: ['proof'] }, // back preview marked as proof
                ],
                uploadLineItem: {
                    name: 'Custom Challenge Coins',
                    lineItem: 'challenge coins',
                    type: 'Made in USA - Simulated Enamel Coin',
                    size: '2"',
                    networkSKUID: models.NetworkSKUID.NETWORK_SK_US_NO_MINIMUM_CUSTOM_COINS,
                    childLineItems: [
                        { child: 'plating', option: platingOption },
                        { child: 'edge', option: 'rope' },
                    ],
                },
                builderDesignID: design.id,
                builderDesignVersion: design.version,
            };
        },
        editingCartItemID: () => editingCartItemID,
        onReplaced: () => {
            editingCartItemID = '';
        },
        afterAdded: () => {
            persistence.clear();
            window.location.href = Relref('/cart');
        },
    });
}

function setupRingColorCards() {
    const $ringColor = document.getElementById('text-ring-color-swatch') as HTMLInputElement | null;
    const $lgRingOverlay = document.getElementById('lg-ring-color-overlay') as HTMLInputElement | null;
    const $ringGraphics = document.querySelectorAll<HTMLElement>('.inline-ring-graphic');
    const $wantsTextRing = document.getElementById('wants-text-ring-switch') as HTMLInputElement | null;
    const $textRingType = document.getElementById('text-ring-type') as HTMLInputElement | null;
    const $noFillToggles = document.querySelectorAll<HTMLInputElement>('#ring-no-fill-toggle, #lg-ring-no-fill-toggle');
    const $ringColorRows = document.querySelectorAll<HTMLElement>('.ring-color-row');

    function onRingColorInput(color: string) {
        if ($wantsTextRing && !$wantsTextRing.checked) {
            $wantsTextRing.checked = true;
            $wantsTextRing.dispatchEvent(new Event('change'));
        }
        if ($textRingType && !$textRingType.checked) {
            $textRingType.checked = true;
            $textRingType.dispatchEvent(new Event('change'));
        }
        $ringGraphics.forEach($g => {
            $g.classList.remove('no-fill');
            $g.style.borderColor = color;
        });
        $noFillToggles.forEach($t => {
            $t.checked = true;
        });
        $ringColorRows.forEach($r => $r.classList.remove('ring-off'));
    }

    if ($ringColor) {
        $ringColor.addEventListener('input', () => {
            if ($lgRingOverlay) {
                $lgRingOverlay.value = $ringColor.value;
            }
            onRingColorInput($ringColor.value);
        });
        $ringColor.addEventListener('click', e => e.stopPropagation());
    }

    if ($lgRingOverlay) {
        $lgRingOverlay.addEventListener('input', () => {
            if ($ringColor) {
                $ringColor.value = $lgRingOverlay.value;
                $ringColor.dispatchEvent(new Event('input'));
            }
        });
        $lgRingOverlay.addEventListener('click', e => e.stopPropagation());
    }
}

// Update color card graphics from swatch values.
// Called on side switch and after restore.
function syncColorGraphics() {
    const $ringColor = document.getElementById('text-ring-color-swatch') as HTMLInputElement | null;
    const $lgRingOverlay = document.getElementById('lg-ring-color-overlay') as HTMLInputElement | null;
    const $lgBgOverlay = document.getElementById('lg-bg-color-overlay') as HTMLInputElement | null;
    const $ringGraphics = document.querySelectorAll<HTMLElement>('.inline-ring-graphic');
    const $wantsTextRing = document.getElementById('wants-text-ring-switch') as HTMLInputElement | null;
    const $textRingType = document.getElementById('text-ring-type') as HTMLInputElement | null;
    const $ringToggle = document.getElementById('ring-toggle') as HTMLInputElement | null;
    const $noFillToggles = document.querySelectorAll<HTMLInputElement>('#ring-no-fill-toggle, #lg-ring-no-fill-toggle');
    const $ringColorRows = document.querySelectorAll<HTMLElement>('.ring-color-row');
    const $ringTextControls = document.querySelector<HTMLElement>('.ring-text-controls');

    const ringOn = $wantsTextRing?.checked ?? true;
    const useColor = $textRingType?.checked ?? true;
    const noFill = ringOn && !useColor;

    // Sync ring on/off toggle
    if ($ringToggle) {
        $ringToggle.checked = ringOn;
    }

    $noFillToggles.forEach($t => {
        $t.checked = !noFill;
    });

    // Hide translucent option when no-fill is active (translucent only makes sense with a filled ring)
    document.querySelectorAll<HTMLElement>('.translucent-toggle-wrapper').forEach($wrapper => {
        $wrapper.classList.toggle('d-none', !ringOn || noFill);
    });

    if ($ringTextControls) {
        $ringTextControls.classList.toggle('ring-disabled', !ringOn);
    }

    if (ringOn) {
        if (noFill) {
            $ringColorRows.forEach($r => $r.classList.add('ring-off'));
            $ringGraphics.forEach($g => {
                $g.classList.add('no-fill');
                $g.style.borderColor = '';
                $g.style.borderWidth = '';
            });
        } else {
            $ringColorRows.forEach($r => $r.classList.remove('ring-off'));
            $ringGraphics.forEach($g => {
                $g.classList.remove('no-fill');
                $g.style.borderWidth = '';
            });
        }
    }

    // No-fill forces color text — metal/cutout isn't possible with a transparent ring
    const $metalColorBtn = document.getElementById('font-metal-color-btn') as HTMLButtonElement | null;
    const $textColorType = document.getElementById('text-color-type') as HTMLInputElement | null;
    if ($metalColorBtn && $textColorType) {
        if (noFill) {
            if (!$textColorType.checked) {
                $textColorType.checked = true;
                $textColorType.dispatchEvent(new Event('change'));
            }
            $metalColorBtn.disabled = true;
            $metalColorBtn.classList.add('disabled');
        } else {
            $metalColorBtn.disabled = false;
            $metalColorBtn.classList.remove('disabled');
        }
    }

    if ($ringColor && ringOn && !noFill) {
        $ringGraphics.forEach($g => {
            $g.style.borderColor = $ringColor.value;
        });
        if ($lgRingOverlay) {
            $lgRingOverlay.value = $ringColor.value;
        }
    }

    if ($bgColor) {
        if ($mobileBgOverlay) {
            $mobileBgOverlay.value = $bgColor.value;
        }
        if ($lgBgOverlay) {
            $lgBgOverlay.value = $bgColor.value;
        }
        if ($bgCircle) {
            $bgCircle.style.backgroundColor = $bgColor.value;
        }
        if ($lgBgGraphic) {
            $lgBgGraphic.style.backgroundColor = $bgColor.value;
        }
    }
    syncBgRemoveButton();
}

function setupPlatingCards() {
    const $platingCards = document.querySelectorAll<HTMLElement>(
        '#design-plating-brass, #design-plating-silver, #lg-plating-brass, #lg-plating-silver',
    );
    const $platingRadios = document.querySelectorAll<HTMLInputElement>('input[name="plating-radio"]');

    if ($platingRadios.length === 0) {
        return;
    }

    function selectPlating(value: string) {
        for (const $radio of $platingRadios) {
            if ($radio.value === value) {
                $radio.checked = true;
                $radio.dispatchEvent(new Event('change'));
                break;
            }
        }

        $platingCards.forEach($card => {
            $card.classList.toggle('active', $card.dataset.value === value);
        });
    }

    $platingCards.forEach($card => {
        $card.addEventListener('click', () => {
            const value = $card.dataset.value;
            if (value) {
                selectPlating(value);
            }
        });
    });
}

function setupRingModeRadios() {
    const $wantsTextRing = document.getElementById('wants-text-ring-switch') as HTMLInputElement | null;
    const $textRingType = document.getElementById('text-ring-type') as HTMLInputElement | null;
    const $textColorType = document.getElementById('text-color-type') as HTMLInputElement | null;
    const $ringToggle = document.getElementById('ring-toggle') as HTMLInputElement | null;
    const $noFillToggles = document.querySelectorAll<HTMLInputElement>('#ring-no-fill-toggle, #lg-ring-no-fill-toggle');

    if (!$wantsTextRing || !$textRingType) {
        return;
    }

    let savedMetalState = false;

    $ringToggle?.addEventListener('change', () => {
        $wantsTextRing.checked = $ringToggle.checked;
        $wantsTextRing.dispatchEvent(new Event('change'));
        syncColorGraphics();
    });

    $noFillToggles.forEach($toggle => {
        $toggle.addEventListener('change', () => {
            if (!$toggle.checked && !$wantsTextRing.checked) {
                $wantsTextRing.checked = true;
                $wantsTextRing.dispatchEvent(new Event('change'));
            }

            if ($textColorType) {
                if (!$toggle.checked) {
                    savedMetalState = !$textColorType.checked;
                } else if (savedMetalState) {
                    $textColorType.checked = false;
                    $textColorType.dispatchEvent(new Event('change'));
                    savedMetalState = false;
                }
            }

            $textRingType.checked = $toggle.checked;
            $textRingType.dispatchEvent(new Event('change'));
            syncColorGraphics();
        });
    });
}

function updateTextSizeIndicators(textCustomizerUI: TextCustomizerUI) {
    const sizes = textCustomizerUI.getLayerFontSizes();
    document.querySelectorAll<HTMLElement>('.text-size-value').forEach($el => {
        const target = $el.dataset.target as 'top' | 'bottom';
        if (target) {
            $el.textContent = String(sizes[target]);
        }
    });
}

function setupTextSizeButtons(textCustomizerUI: TextCustomizerUI) {
    const $sizeButtons = document.querySelectorAll<HTMLButtonElement>('.text-size-btn');

    // Available font sizes in pt (must match TextCustomizerUI's available sizes)
    const fontSizesPt = [4, 5, 6, 7, 8, 9, 10, 11];

    $sizeButtons.forEach($btn => {
        $btn.addEventListener('click', () => {
            const target = ($btn.dataset.target ?? 'top') as 'top' | 'bottom';
            const sizes = textCustomizerUI.getLayerFontSizes();
            const currentSizePt = sizes[target];
            const currentIndex = fontSizesPt.indexOf(currentSizePt);

            const isUp = $btn.classList.contains('text-size-up');
            let newIndex: number;

            if (isUp) {
                newIndex = currentIndex < fontSizesPt.length - 1 ? currentIndex + 1 : currentIndex;
            } else {
                newIndex = currentIndex > 0 ? currentIndex - 1 : currentIndex;
            }

            if (newIndex !== currentIndex) {
                void textCustomizerUI.setLayerFontSize(target, fontSizesPt[newIndex]).then(() => {
                    updateTextSizeIndicators(textCustomizerUI);
                });
            }
        });
    });

    updateTextSizeIndicators(textCustomizerUI);
}

function setupTextColorIndicators(textCustomizerUI: TextCustomizerUI) {
    const $mobileColorInputs = document.querySelectorAll<HTMLInputElement>('.text-color-input');
    const $textColorType = document.getElementById('text-color-type') as HTMLInputElement | null;

    $mobileColorInputs.forEach($input => {
        $input.addEventListener('input', () => {
            const color = $input.value;
            const target = $input.dataset.target;

            // Ensure text is in Color mode (not Metal mode)
            if ($textColorType && !$textColorType.checked) {
                $textColorType.checked = true;
                $textColorType.dispatchEvent(new Event('change'));
            }

            if (target === 'bottom') {
                textCustomizerUI.setBottomTextColor(color);
            } else {
                textCustomizerUI.setTopTextColor(color);
            }

            const $indicator = $input.closest('.text-color-indicator');
            const $colorBar = $indicator?.querySelector('.color-bar') as HTMLElement | null;
            if ($colorBar) {
                $colorBar.style.backgroundColor = color;
            }
        });
    });
}

function setupMetalColorToggle() {
    const $metalColorBtn = document.getElementById('font-metal-color-btn');
    const $textColorType = document.getElementById('text-color-type') as HTMLInputElement | null;

    if (!$metalColorBtn || !$textColorType) {
        return;
    }

    $metalColorBtn.addEventListener('click', () => {
        $textColorType.checked = !$textColorType.checked;
        $textColorType.dispatchEvent(new Event('change'));
    });

    // Sync button active state whenever the underlying checkbox changes
    // (can be triggered by the button click above, mobile color pickers, or side switch)
    // checked = metal mode
    $metalColorBtn.classList.toggle('active', !$textColorType.checked);
    $textColorType.addEventListener('change', () => {
        $metalColorBtn.classList.toggle('active', !$textColorType.checked);
    });
}

// Wire design tab background color card — syncs circle fill and provides
// a trash button to remove the bg color entirely.
// All sizes use direct overlay inputs for proper Chrome picker anchoring.
function setupDesignBgCard() {
    const $lgBgOverlay = document.getElementById('lg-bg-color-overlay') as HTMLInputElement | null;

    function applyBgColor(color: string) {
        if ($bgColor) {
            $bgColor.value = color;
        }
        if ($mobileBgOverlay) {
            $mobileBgOverlay.value = color;
        }
        if ($lgBgOverlay) {
            $lgBgOverlay.value = color;
        }
        if ($bgCircle) {
            $bgCircle.style.backgroundColor = color;
        }
        if ($lgBgGraphic) {
            $lgBgGraphic.style.backgroundColor = color;
        }
        syncBgRemoveButton();
    }

    // Mobile overlay input (direct tap)
    $mobileBgOverlay?.addEventListener('input', () => {
        applyBgColor($mobileBgOverlay.value);
        $bgColor?.dispatchEvent(new Event('input'));
    });
    // LG overlay input (direct tap)
    $lgBgOverlay?.addEventListener('input', () => {
        applyBgColor($lgBgOverlay.value);
        $bgColor?.dispatchEvent(new Event('input'));
    });
    // Hidden swatch (used by BackgroundColorUI)
    $bgColor?.addEventListener('input', () => applyBgColor($bgColor.value));

    function resetBg(e: Event) {
        e.stopPropagation();
        if ($bgToggle?.checked) {
            $bgToggle.checked = false;
            $bgToggle.dispatchEvent(new Event('change'));
        }
        applyBgColor('#FFFFFF');
    }

    $bgRemove?.addEventListener('click', resetBg);
    $lgBgRemove?.addEventListener('click', resetBg);

    syncBgRemoveButton();
}

function syncBgRemoveButton() {
    const enabled = $bgToggle?.checked ?? false;
    $bgRemove?.classList.toggle('d-none', !enabled);
    $lgBgRemove?.classList.toggle('d-none', !enabled);
}

void main();
